/**
 * PriceOdometer — animates a monetary value with a per-digit rolling
 * effect when the value changes. Used in the plan builder total line
 * and the checkout drawer subtotal.
 *
 * Design constraints (owner spec, Phase A tail #6 + B3):
 *  • Value → renders as `$X.YY` (two decimal fixed).
 *  • On change, each digit that DIFFERS from the previous render slides
 *    from bottom (old digit exits up, new digit enters from below) with
 *    a 320ms cubic ease. Digits that stay identical don't animate — so
 *    a jump from $84.49 to $89.99 only animates the tens, ones, tenths,
 *    and hundredths (four wheels), keeping the "$" and "." static.
 *  • Respects `prefers-reduced-motion` — under reduce we swap to a
 *    plain text render with an opacity flash instead of the roll.
 *  • Uses `tabular-nums` so digit widths don't reflow while animating.
 *
 * Accessibility: the visible number is duplicated in a screen-reader
 * only `<span>` (aria-live="polite") so a rider using VoiceOver hears
 * the new total once per change, not once per digit.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

function formatValue(value, prefix, decimals) {
  const n = Number.isFinite(value) ? value : 0;
  return `${prefix}${n.toFixed(decimals)}`;
}

export default function PriceOdometer({
  value,
  prefix = "$",
  decimals = 2,
  className = "",
  testid = "price-odometer",
}) {
  const reduced = useReducedMotion();
  const formatted = useMemo(
    () => formatValue(value, prefix, decimals),
    [value, prefix, decimals],
  );
  // Track the previous formatted string so we know which chars changed.
  const prevRef = useRef(formatted);
  const [render, setRender] = useState(formatted);

  useEffect(() => {
    prevRef.current = render;
    setRender(formatted);
  }, [formatted]);

  // Reduced-motion path: plain text with a brief opacity blink to signal
  // that the number changed. No sliding wheels.
  if (reduced) {
    return (
      <motion.span
        className={`inline-flex tabular-nums ${className}`}
        key={formatted}
        initial={{ opacity: 0.35 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.12 }}
        data-testid={testid}
        data-reduced="true"
      >
        <span className="sr-only" aria-live="polite">{formatted}</span>
        <span aria-hidden="true">{formatted}</span>
      </motion.span>
    );
  }

  const prev = prevRef.current;
  return (
    <span
      className={`inline-flex tabular-nums ${className}`}
      data-testid={testid}
      data-reduced="false"
    >
      {/* SR-only live region so screen readers hear the delta cleanly. */}
      <span className="sr-only" aria-live="polite">{formatted}</span>
      <span aria-hidden="true" className="inline-flex">
        {formatted.split("").map((ch, i) => {
          const prevCh = prev[i];
          const changed = prevCh !== ch;
          // Non-digit characters ("$", ".") never animate.
          if (!/\d/.test(ch)) {
            return <span key={`s-${i}-${ch}`}>{ch}</span>;
          }
          return (
            <span
              key={`d-${i}`}
              className="relative inline-block overflow-hidden align-baseline"
              style={{ width: "0.62em", height: "1.05em", lineHeight: "1.05em" }}
            >
              <AnimatePresence initial={false} mode="popLayout">
                <motion.span
                  key={ch}
                  className="absolute inset-0 flex items-baseline justify-center"
                  initial={changed ? { y: "100%", opacity: 0 } : { y: 0, opacity: 1 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: "-100%", opacity: 0 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                >
                  {ch}
                </motion.span>
              </AnimatePresence>
            </span>
          );
        })}
      </span>
    </span>
  );
}
